import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { Subject } from "rxjs";
import { TemplatePortal } from "@angular/cdk/portal";
import { ProjectsService } from "app/core/services/projects.service";
import { Store } from "@ngrx/store";
import { AppState } from "app/core/store/reducers/app.reducer";
import { SetProject } from "app/core/store/actions/common.actions";
import { LocalStorageService } from "app/core/services/local-storage.service";
import { LOCAL_STORAGE_KEYS } from "app/core/constants/local-storage-keys";
import { filter } from "rxjs/operators";
import { selectProject } from "app/core/store/selectors";
import { FormsService } from "app/core/services/forms.service";
import _ from "lodash";

@Component({
    selector: "app-project-starter",
    templateUrl: "./project-starter.component.html",
    styleUrls: ["./project-starter.component.scss"],
})
export class ProjectStarterComponent implements OnInit, OnDestroy {
    @ViewChild("tagsPanelOrigin") private _tagsPanelOrigin: ElementRef;
    @ViewChild("tagsPanel") private _tagsPanel: TemplateRef<any>;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    projects: any[] = [];
    selectedProject: any;
    private _tagsPanelOverlayRef: OverlayRef;
    filteredTags: any[];
    tagsEditMode: boolean = false;
    searchText: string;
    projectSub: any;

    constructor(
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private projectService: ProjectsService,
        private formsService: FormsService,
        private store: Store<AppState>,
        private localStorage: LocalStorageService
    ) {}

    ngOnInit(): void {
        this.formsService.list().subscribe((forms) => {
            this.projects = forms.map((x) => x.project);
            this.projects.unshift({
                id: "0",
                name: "All Projects",
                code: "All",
            });

            this.projects = _.uniqBy(this.projects, "code");

            this.projectSub = this.store
                .select(selectProject)
                .subscribe((x) => {
                    if (x) {
                        this.selectedProject = x;
                    } else {
                        this.selectedProject = this.projects[0];
                    }
                });
        });
    }

    openTagsPanel(): void {
        // Create the overlay
        this._tagsPanelOverlayRef = this._overlay.create({
            backdropClass: "",
            hasBackdrop: true,
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._tagsPanelOrigin.nativeElement)
                .withFlexibleDimensions()
                .withViewportMargin(64)
                .withLockedPosition()
                .withPositions([
                    {
                        originX: "start",
                        originY: "bottom",
                        overlayX: "start",
                        overlayY: "top",
                    },
                ]),
        });

        // Subscribe to the attachments observable
        this._tagsPanelOverlayRef.attachments().subscribe(() => {
            // Focus to the search input once the overlay has been attached
            this._tagsPanelOverlayRef.overlayElement
                .querySelector("input")
                .focus();
        });

        // Create a portal from the template
        const templatePortal = new TemplatePortal(
            this._tagsPanel,
            this._viewContainerRef
        );

        // Attach the portal to the overlay
        this._tagsPanelOverlayRef.attach(templatePortal);

        // Subscribe to the backdrop click
        this._tagsPanelOverlayRef.backdropClick().subscribe(() => {
            // If overlay exists and attached...
            if (
                this._tagsPanelOverlayRef &&
                this._tagsPanelOverlayRef.hasAttached()
            ) {
                // Detach it
                this._tagsPanelOverlayRef.detach();

                // Reset the tag filter
                this.filteredTags = this.projects;

                // Toggle the edit mode off
                this.tagsEditMode = false;
            }

            // If template portal exists and attached...
            if (templatePortal && templatePortal.isAttached) {
                // Detach it
                templatePortal.detach();
            }
        });
    }

    /**
     * Toggle the tags edit mode
     */
    toggleTagsEditMode(): void {
        this.tagsEditMode = !this.tagsEditMode;
    }

    /**
     * Filter tags
     *
     * @param event
     */
    filterTags(event): void {
        // Get the value
        const value = event.target.value.toLowerCase();

        // Filter the tags
        this.filteredTags = this.projects.filter((tag) =>
            tag.toLowerCase().includes(value)
        );
    }

    selectProject = (project: any): void => {
        this.selectedProject = project;
        this.localStorage.set(
            LOCAL_STORAGE_KEYS.SelectedProject,
            this.selectedProject
        );
        this.store.dispatch(new SetProject(project));
        this._tagsPanelOverlayRef.dispose();
    };

    ngOnDestroy() {
        this.projectSub?.unsubscribe();
    }
}
